import z from "zod";

import {
  ageRangeEnumSchema,
  americanIndianOrAlaskaNativeStatusEnumSchema,
  applicationLastStepEnumSchema,
  applicationSchema,
  applicationTypeEnumSchema,
  auditTrailAuthEventSchema,
  blindOrVisionLossStatusEnumSchema,
  breastCancerStatusEnumSchema,
  cervicalCancerStatusEnumSchema,
  childCaregiverStatusEnumSchema,
  citizenshipStatusEnumSchema,
  consentTypeEnumSchema,
  coverageCheckSchema,
  coverageCheckServiceTypeSelectSchema,
  coverageStatusEnumSchema,
  disabledStatusEnumSchema,
  documentGroupEnumSchema,
  documentTypeEnumSchema,
  employmentStatusEnumSchema,
  federalGroupEnumSchema,
  fosterCareFormerlyStatusEnumSchema,
  healthInsuranceOrCoverageStatusEnumSchema,
  householdMemberSchema,
  livingWithStatusEnumSchema,
  maritalStatusEnumSchema,
  medicalExpenses3MonthsHelpNeededStatusEnumSchema,
  medicarePlanTypeEnumSchema,
  needsHelpDailyActivitiesStatusEnumSchema,
  payFrequencyEnumSchema,
  pregnantStatusEnumSchema,
  redeterminationStatusEnumSchema,
  relationshipEnumSchema,
  studentFullTime18to20StatusEnumSchema,
  taxClaimedAsDependentStatusEnumSchema,
  taxDependentsClaimedStatusEnumSchema,
  taxFilingStatusEnumSchema,
  taxRelationshipEnumSchema,
  unemploymentCompensationStatusEnumSchema,
  veteranOrMilitaryMemberStatusEnumSchema,
  workRequirementExemptionEnumSchema,
  workRequirementMemberStatusEnumSchema,
  workRequirementScreeningStatusEnumSchema,
  workRequirementScreeningStepEnumSchema,
} from "@libs/db-schemas";

export {
  americanIndianOrAlaskaNativeStatusEnumSchema,
  applicationLastStepEnumSchema,
  documentGroupEnumSchema,
  documentTypeEnumSchema,
  needsHelpDailyActivitiesStatusEnumSchema,
  relationshipEnumSchema,
  studentFullTime18to20StatusEnumSchema,
  veteranOrMilitaryMemberStatusEnumSchema,
  pregnantStatusEnumSchema,
  breastCancerStatusEnumSchema,
  cervicalCancerStatusEnumSchema,
  blindOrVisionLossStatusEnumSchema,
  disabledStatusEnumSchema,
  fosterCareFormerlyStatusEnumSchema,
  maritalStatusEnumSchema,
  ageRangeEnumSchema,
  taxFilingStatusEnumSchema,
  taxDependentsClaimedStatusEnumSchema,
  taxClaimedAsDependentStatusEnumSchema,
  taxRelationshipEnumSchema,
  livingWithStatusEnumSchema,
  employmentStatusEnumSchema,
  unemploymentCompensationStatusEnumSchema,
  payFrequencyEnumSchema,
  medicalExpenses3MonthsHelpNeededStatusEnumSchema,
  citizenshipStatusEnumSchema,
  healthInsuranceOrCoverageStatusEnumSchema,
  medicarePlanTypeEnumSchema,
  childCaregiverStatusEnumSchema,
  federalGroupEnumSchema,
  auditTrailAuthEventSchema,
  redeterminationStatusEnumSchema,
  consentTypeEnumSchema,
  workRequirementExemptionEnumSchema,
  workRequirementMemberStatusEnumSchema,
  workRequirementScreeningStatusEnumSchema,
  workRequirementScreeningStepEnumSchema,
};

export const coverageCheckServiceTypeSchema =
  coverageCheckServiceTypeSelectSchema.pick({
    code: true,
    name: true,
    description: true,
  });

export const coverageDetailsSchema = coverageCheckSchema
  .pick({
    workspaceId: true,
    payerId: true,
    applicationType: true,
    checkCompletionDate: true,
    coverageEndDate: true,
    aidCodes: true,
    plan: true,
    primaryCareProvider: true,
  })
  .extend({
    serviceTypes: z.array(coverageCheckServiceTypeSchema),
  });

export const memberCoverageSchema = z.object({
  status: coverageStatusEnumSchema,
  lastChecked: z.string().nullable(),
  coveredPrograms: z.array(coverageDetailsSchema),
});

export const householdMemberWithCoverageSchema = householdMemberSchema.extend({
  coverage: memberCoverageSchema.nullish(),
});

export const householdMemberDocumentRequirementsSectionSchema = z.object({
  group: documentGroupEnumSchema,
  isApplicable: z.boolean(),
  isComplete: z.boolean(),
  acceptedTypes: documentTypeEnumSchema.array(),
  completedByTypes: documentTypeEnumSchema.array(),
});

export const householdMemberDocumentRequirementsSchema = z.object({
  householdMemberId: z.uuid(),
  applicableCount: z.number().int(),
  completeCount: z.number().int(),
  sections: z.array(householdMemberDocumentRequirementsSectionSchema),
});

export const applicationApplicantSchema = z.object({
  householdMemberId: z.uuid(),
  firstName: z.string(),
});

export const listApplicationSchema = applicationSchema
  .pick({
    id: true,
    patientId: true,
    applicationType: true,
    applicationState: true,
    applicationStatus: true,
    npsSurveyId: true,
    submittedAt: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    applicants: z.array(applicationApplicantSchema),
    brands: z.array(
      z.object({
        customUsStateName: z.string().nullable(),
        federalGroup: federalGroupEnumSchema,
      }),
    ),
  });

export const listApplicationsResponseSchema = z.array(listApplicationSchema);

export const recentDuplicateApplicationQuerySchema = z.object({
  selectedApplications: z
    .array(
      z.object({
        applicationType: applicationTypeEnumSchema,
        householdMemberIds: z.array(z.uuid()).min(1),
      }),
    )
    .min(1)
    .max(3),
});

export const recentDuplicateApplicationSchema = z.object({
  applicationType: applicationTypeEnumSchema,
  applicants: z.array(applicationApplicantSchema).min(1),
});

export const recentDuplicateApplicationsSchema = z.array(
  recentDuplicateApplicationSchema,
);

export const healthPlanAvailabilitySchema = z.object({
  count: z.number().int().nonnegative(),
});

export const usernameAccountStatusSchema = z.object({
  hasAccount: z.boolean(),
});
